import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../../../components/common/Drawer";
import Badge from "../../../components/common/Badge";
import Tab from "../../../components/common/Tab";
import JobStatusChip from "./JobStatusChip";
import {
  fetchJobDetailsThunk,
  fetchJobAuditsThunk,
} from "../../../store/thunks/campaignThunks";
import { formatNumber } from "../../../services/analyticsService";
import {
  CalendarIcon,
  UserGroupIcon,
  DeviceMobileIcon,
  DocumentTextIcon,
  ClockIcon,
} from "@heroicons/react/outline";

const JobDetailDrawer = ({ isOpen, onClose, jobId }) => {
  const dispatch = useDispatch();
  const { selectedJob, jobAudits, loading } = useSelector(
    (state) => state.notificationCampaigns
  );
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (isOpen && jobId) {
      dispatch(fetchJobDetailsThunk(jobId));
      dispatch(fetchJobAuditsThunk({ jobId }));
    }
  }, [isOpen, jobId, dispatch]);

  if (!selectedJob) {
    return null;
  }

  const tabs = [
    {
      label: "Overview",
      content: (
        <div className="space-y-6">
          {/* Status */}
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Status</h4>
            <JobStatusChip status={selectedJob.status} size="lg" />
          </div>

          {/* Metrics */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-500">Total Recipients</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatNumber(selectedJob.stats?.total || 0)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-500">Sent</p>
              <p className="text-2xl font-bold text-success">
                {formatNumber(selectedJob.stats?.sent || 0)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-500">Failed</p>
              <p className="text-2xl font-bold text-danger">
                {formatNumber(selectedJob.stats?.failed || 0)}
              </p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-4">
              <p className="text-sm text-gray-500">Opened</p>
              <p className="text-2xl font-bold text-primary">
                {formatNumber(selectedJob.stats?.opened || 0)}
              </p>
            </div>
          </div>

          {/* Details */}
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">
                  {new Date(selectedJob.createdAt).toLocaleString()}
                </p>
              </div>
            </div>
            {selectedJob.completedAt && (
              <div className="flex items-center gap-3">
                <ClockIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="font-medium">
                    {new Date(selectedJob.completedAt).toLocaleString()}
                  </p>
                </div>
              </div>
            )}
            {selectedJob.templateKey && (
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Template</p>
                  <p className="font-medium">
                    {selectedJob.templateKey} (v{selectedJob.templateVersion})
                  </p>
                </div>
              </div>
            )}
            {selectedJob.hasCustomContent && (
              <div className="flex items-center gap-3">
                <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                <div>
                  <p className="text-sm text-gray-500">Custom Content</p>
                  <p className="font-medium">{selectedJob.hasCustomContent}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ),
    },
    {
      label: "Audience",
      content: (
        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Segments</h4>
            <div className="flex flex-wrap gap-2">
              {selectedJob.audience?.segments?.map((segment) => (
                <Badge key={segment} variant="info">
                  {segment}
                </Badge>
              )) || <span className="text-gray-500">All users</span>}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">Channels</h4>
            <div className="flex gap-2">
              {selectedJob.channels?.map((channel) => (
                <Badge key={channel} variant="secondary">
                  {channel}
                </Badge>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-medium text-gray-500 mb-2">
              Platforms
            </h4>
            <div className="flex gap-2">
              {selectedJob.audience?.platforms?.map((platform) => (
                <Badge key={platform} variant="secondary">
                  {platform}
                </Badge>
              ))}
            </div>
          </div>
          {selectedJob.audience?.testDevices?.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">
                Test Devices
              </h4>
              <ul className="space-y-1">
                {selectedJob.audience.testDevices.map((device) => (
                  <li key={device} className="text-sm text-gray-600">
                    {device}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {selectedJob.customerIds && selectedJob.customerIds.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-500 mb-2">
                Customer Count
              </h4>
              <p className="text-sm">
                {selectedJob.customerIds.length} customers targeted
              </p>
            </div>
          )}
        </div>
      ),
    },
    {
      label: "Audit Log",
      content: (
        <div className="space-y-2">
          {jobAudits.map((audit, index) => (
            <div
              key={index}
              className="border-l-2 border-gray-200 dark:border-gray-700 pl-4 py-2"
            >
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium text-sm">
                    {audit.event} via{" "}
                    {audit.channel === "NONE" ? "N/A" : audit.channel}
                  </p>
                  {audit.errorCode && (
                    <p className="text-xs text-danger">
                      {audit.errorCode}: {audit.errorMessage || "Unknown error"}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    Customer ID: {audit.customerId}
                  </p>
                </div>
                <span className="text-xs text-gray-400">
                  {new Date(audit.createdAt).toLocaleString()}
                </span>
              </div>
            </div>
          ))}
          {jobAudits.length === 0 && (
            <p className="text-center text-gray-500 py-4">
              No audit logs available
            </p>
          )}
        </div>
      ),
    },
  ];

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={`Job Details - ${selectedJob.id}`}
      size="md"
    >
      {loading ? (
        <div className="space-y-4">
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
          <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse" />
        </div>
      ) : (
        <Tab tabs={tabs} onTabChange={setActiveTab} />
      )}
    </Drawer>
  );
};

export default JobDetailDrawer;
